import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { RecordingPlayer } from "../audio/RecordingPlayer";
import { cn } from "../../lib/utils";

export type ComposerSubmission =
  | { kind: "audio"; blob: Blob }
  | { kind: "text"; text: string };

/**
 * Pick a MediaRecorder mimeType the current browser actually supports.
 * iOS Safari does NOT support webm — only mp4/AAC. Chrome/Firefox prefer
 * webm/opus.
 */
function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // Some Safari versions throw on isTypeSupported for unknown strings.
    }
  }
  return undefined;
}

export type ComposerProps = {
  onSubmit: (sub: ComposerSubmission) => void;
  onRecordingChange?: (recording: boolean) => void;
  disabled?: boolean;
  showFirstTurnHint?: boolean;
  timeUp?: boolean;
};

/**
 * Two-mode answer composer mirroring real DUO Spreken UX:
 *
 *   idle  →  recording  →  preview  →  submit
 *                  (auto-stops at 60s, lands in preview — never auto-submits)
 */
export function Composer({
  onSubmit,
  onRecordingChange,
  disabled,
  showFirstTurnHint,
  timeUp,
}: ComposerProps) {
  const [mode, setMode] = useState<"voice" | "text">("voice");
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [pendingBlobUrl, setPendingBlobUrl] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    return () => {
      if (pendingBlobUrl) URL.revokeObjectURL(pendingBlobUrl);
    };
  }, [pendingBlobUrl]);

  function discardPending() {
    if (pendingBlobUrl) URL.revokeObjectURL(pendingBlobUrl);
    setPendingBlob(null);
    setPendingBlobUrl(null);
    setError(null);
  }

  async function startRecording() {
    setError(null);
    discardPending();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError(
        "Geen toegang tot microfoon. Schakel om naar typen of geef toestemming.",
      );
      setMode("text");
      return;
    }

    const mime = pickRecorderMime();
    let rec: MediaRecorder;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      console.warn("[composer] MediaRecorder construct failed", err);
      setError(
        "Opname wordt door deze browser niet ondersteund. Schakel om naar typen.",
      );
      setMode("text");
      return;
    }

    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onerror = (e) => {
      console.warn("[composer] MediaRecorder error", e);
    };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blobType = rec.mimeType || mime || "";
      const blob = blobType
        ? new Blob(chunksRef.current, { type: blobType })
        : new Blob(chunksRef.current);
      chunksRef.current = [];
      if (blob.size === 0) {
        setError("Geen audio opgenomen. Probeer het opnieuw.");
        return;
      }
      const url = URL.createObjectURL(blob);
      setPendingBlob(blob);
      setPendingBlobUrl(url);
    };
    rec.start(1000);
    recRef.current = rec;
    setRecording(true);
    onRecordingChange?.(true);
  }

  function stopRecording() {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
    onRecordingChange?.(false);
  }

  function abortRecording() {
    const rec = recRef.current;
    if (rec) {
      rec.onstop = () => {
        rec.stream?.getTracks().forEach((t) => t.stop());
      };
      if (rec.state !== "inactive") rec.stop();
    }
    chunksRef.current = [];
    setRecording(false);
    onRecordingChange?.(false);
  }

  useEffect(() => {
    if (!recording) return;
    const t = setTimeout(() => stopRecording(), 60_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  useEffect(() => {
    if (!timeUp) return;
    abortRecording();
    discardPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUp]);

  function submitPending() {
    if (!pendingBlob) return;
    onSubmit({ kind: "audio", blob: pendingBlob });
    setPendingBlob(null);
    setPendingBlobUrl(null);
  }

  function submitText() {
    const t = text.trim();
    if (!t) {
      setError("Voer eerst een antwoord in.");
      return;
    }
    onSubmit({ kind: "text", text: t });
  }

  const modeLocked = recording || pendingBlob !== null;

  const stateLine = recording
    ? "Aan het opnemen… spreek rustig."
    : pendingBlob
      ? "Beluister uw opname, neem opnieuw op of verstuur."
      : "Klaar? Tik op de microfoon en geef uw antwoord.";

  return (
    <div className="space-y-4">
      {mode === "voice" ? (
        <div className="flex flex-col items-center gap-5 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-6 shadow-[var(--shadow)] sm:p-7">
          <p className="text-sm text-[var(--ink-muted)]" role="status" aria-live="polite">
            {stateLine}
          </p>

          {pendingBlob && pendingBlobUrl ? (
            <>
              <RecordingPlayer src={pendingBlobUrl} pausedLabel="Beluister" />
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                  onClick={() => {
                    discardPending();
                    void startRecording();
                  }}
                  variant="secondary"
                  size="lg"
                  aria-label="Verwijder de opname en neem opnieuw op"
                >
                  ↻ Opnieuw opnemen
                </Button>
                <Button
                  onClick={submitPending}
                  variant="warm"
                  size="lg"
                  aria-label="Verstuur deze opname"
                >
                  Antwoord versturen
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-5">
                {recording ? <RecordingWave /> : null}
                <span className="relative inline-flex">
                  {recording ? (
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full bg-[var(--fail)] opacity-60 animate-ping motion-reduce:animate-none"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={disabled}
                    aria-label={
                      recording
                        ? "Stop de opname"
                        : "Start opname van uw gesproken antwoord"
                    }
                    aria-pressed={recording}
                    className={cn(
                      "relative grid h-[76px] w-[76px] place-items-center rounded-full text-white transition-[transform,background-color] duration-150 ease-out hover:scale-105 active:scale-100 motion-reduce:transform-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                      recording
                        ? "bg-[var(--fail)]"
                        : "bg-[var(--accent)] shadow-[var(--shadow-blue)]",
                    )}
                  >
                    {recording ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <rect x="7" y="7" width="10" height="10" rx="2" />
                      </svg>
                    ) : (
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <rect x="9" y="3" width="6" height="11" rx="3" />
                        <path d="M6 11a6 6 0 0012 0M12 17v4" />
                      </svg>
                    )}
                  </button>
                </span>
              </div>
              {recording ? (
                <Button onClick={stopRecording} variant="warm" size="lg">
                  Antwoord versturen
                </Button>
              ) : null}
              {showFirstTurnHint && !recording ? (
                <span className="text-sm text-[var(--ink-muted)]">
                  60 seconden — net als bij het echte examen.
                </span>
              ) : null}
            </>
          )}

          {!modeLocked ? (
            <button
              type="button"
              onClick={() => setMode("text")}
              disabled={disabled}
              className="text-sm text-[var(--ink-muted)] underline-offset-4 hover:text-[var(--ink)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
            >
              Liever typen?
            </button>
          ) : null}

          <span role="status" aria-live="polite" className="sr-only">
            {recording
              ? "Opname loopt."
              : pendingBlob
                ? "Opname klaar voor controle. Beluister, neem opnieuw op of verstuur."
                : ""}
          </span>
        </div>
      ) : (
        <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-6 shadow-[var(--shadow)]">
          <label htmlFor="composer-text" className="sr-only">
            Schrijf uw antwoord in het Nederlands
          </label>
          <textarea
            id="composer-text"
            className="w-full min-h-32 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--panel-soft)] p-3 text-[var(--ink)] placeholder:text-[var(--ink-faint)] focus:border-[var(--accent)] focus:bg-[var(--panel)] outline-none"
            placeholder="Schrijf uw antwoord in het Nederlands..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={disabled}
            rows={5}
            maxLength={2000}
            aria-describedby="composer-text-counter"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button onClick={submitText} disabled={disabled} variant="warm" size="lg">
                Antwoord versturen
              </Button>
              <button
                type="button"
                onClick={() => setMode("voice")}
                disabled={disabled}
                className="text-sm text-[var(--ink-muted)] underline-offset-4 hover:text-[var(--ink)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
              >
                Liever spreken?
              </button>
            </div>
            <span
              id="composer-text-counter"
              className="text-xs font-mono-badge text-[var(--ink-faint)]"
              aria-label={`${text.length} van 2000 tekens gebruikt`}
            >
              {text.length}/2000
            </span>
          </div>
        </div>
      )}

      {error ? (
        <p role="alert" className="text-sm text-[var(--fail)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// Why: a fixed varied-height profile reads as a waveform without any
// per-frame height animation (brand bans width/height transitions). The
// breathing comes from staggered opacity via Tailwind's animate-pulse,
// which reduced-motion neutralises automatically.
const WAVE_HEIGHTS = [40, 70, 55, 88, 48, 72, 36, 80, 52, 44];

function RecordingWave() {
  return (
    <div className="flex h-[46px] min-w-[110px] items-center gap-1" aria-hidden>
      {WAVE_HEIGHTS.map((h, i) => (
        <i
          key={i}
          className="block w-1 rounded-full bg-[var(--accent)] animate-pulse motion-reduce:animate-none"
          style={{ height: `${h}%`, animationDelay: `${(i % 5) * 0.12}s` }}
        />
      ))}
    </div>
  );
}
